import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

export interface PersonEntry {
  key: string;
  name: string;
  initials: string;
  color: string;
  photo?: string | null;
  status: 'assigned' | 'completed';
  date: string | null;
  // Extra line under the name, e.g. who an assigner assigned
  detail?: string;
}

interface PeopleHoverCardProps {
  title: string;
  // Latest first — the first person is shown in the cell, everyone in the card
  people: PersonEntry[];
  emptyLabel: string;
  // Tick on the avatar of people who completed an inspection
  showTicks?: boolean;
}

const CARD_WIDTH = 320;

const fmtDate = (iso: string | null) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const Avatar: React.FC<{ person: PersonEntry; size: string; tick: boolean }> = ({ person, size, tick }) => {
  const [broken, setBroken] = useState(false);
  return (
    <div className="relative shrink-0">
      {person.photo && !broken ? (
        <img
          src={person.photo}
          alt={person.name}
          onError={() => setBroken(true)}
          className={`${size} rounded-full object-cover border border-slate-200`}
        />
      ) : (
        <div className={`${size} flex items-center justify-center rounded-full font-bold ${person.color}`}>
          {person.initials}
        </div>
      )}
      {tick && (
        <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-white">
          <span className="material-symbols-outlined text-[10px] font-bold text-white">check</span>
        </span>
      )}
    </div>
  );
};

// One person with an Assigned/Completed pill — the hover card's rows, also used by the PO calendar panel.
export const PersonRow: React.FC<{ person: PersonEntry; tick: boolean; highlight?: boolean }> = ({ person, tick, highlight = false }) => (
  <div className={`flex items-center gap-3 rounded-xl px-2.5 py-2 ${highlight ? 'bg-slate-50' : ''}`}>
    <Avatar person={person} size="h-9 w-9 text-xs" tick={tick} />
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold text-slate-800">{person.name}</p>
      {person.detail && <p className="truncate text-[11px] text-slate-500">→ {person.detail}</p>}
    </div>
    <div className="shrink-0 text-right">
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold ring-1 ${
          person.status === 'completed'
            ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
            : 'bg-blue-50 text-blue-700 ring-blue-200'
        }`}
      >
        {person.status === 'completed' ? (
          <span className="material-symbols-outlined text-[12px]">check</span>
        ) : (
          <span className="h-1.5 w-1.5 rounded-full bg-blue-500"></span>
        )}
        {person.status === 'completed' ? 'Completed' : 'Assigned'}
      </span>
      {person.date && <p className="mt-1 text-[10px] font-medium text-slate-400">{fmtDate(person.date)}</p>}
    </div>
  </div>
);

const PeopleHoverCard: React.FC<PeopleHoverCardProps> = ({ title, people, emptyLabel, showTicks = false }) => {
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // The card is fixed-positioned, so hide it rather than leave it behind on scroll
  useEffect(() => {
    if (!popupPos) return;
    const hide = () => setPopupPos(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [popupPos]);

  if (people.length === 0) {
    return (
      <span className="inline-flex items-center text-sm text-slate-400 italic">
        {emptyLabel}
      </span>
    );
  }

  const handleMouseEnter = () => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const cardHeight = 56 + people.length * 56;

    // Below the cell; above it when there's no room; clamped to the viewport
    let top = rect.bottom + 8;
    if (top + cardHeight > window.innerHeight - 16) {
      top = Math.max(16, rect.top - cardHeight - 8);
    }
    const left = Math.max(16, Math.min(rect.left, window.innerWidth - CARD_WIDTH - 16));
    setPopupPos({ top, left });
  };

  const first = people[0];

  const popup = popupPos
    ? ReactDOM.createPortal(
        <div
          className="fixed z-[9999] rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden pointer-events-none animate-in fade-in zoom-in-95 duration-150"
          style={{ top: popupPos.top, left: popupPos.left, width: CARD_WIDTH }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white">
            <p className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">{title}</p>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">
              {people.length}
            </span>
          </div>

          {/* People */}
          <div className="p-2 space-y-0.5">
            {people.map((p, i) => (
              <PersonRow key={p.key} person={p} tick={showTicks && p.status === 'completed'} highlight={i === 0} />
            ))}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div
      ref={wrapperRef}
      className="flex items-center gap-2"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setPopupPos(null)}
    >
      <Avatar person={first} size="h-8 w-8 text-xs" tick={showTicks && first.status === 'completed'} />
      <div className="text-sm text-slate-700">{first.name}</div>
      {people.length > 1 && <span className="text-xs text-slate-400">+{people.length - 1}</span>}
      {popup}
    </div>
  );
};

export default PeopleHoverCard;
